import type { ProductJourney } from "@shared/analytics/productEvents";
import {
    useProductJourney,
    useProductObservation
} from "@shared/analytics/useProductJourney";
import { List, ListItem } from "@shared/components/Data/List";
import {
    type AccountType,
    AccountTypeBadge
} from "@shared/components/Yaas/AccountTypeBadge";
import { useSolanaAddressVerification } from "@shared/hooks/useSolanaAddressVerification";
import { SolanaAddress } from "@shared/utils/address";
import { useDirtyState } from "@solomon-apps/shared";
import { CopyIcon } from "@solomon-labs/design/components/icons";
import { SectionHeading } from "@solomon-labs/design/components/layout";
import {
    Button,
    ButtonIcon,
    Card,
    Text,
    textVariants,
    toastManager,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@solomon-labs/design/components/ui";
import { AnimatePresence, motion } from "motion/react";
import React, { useCallback, useEffect, useState } from "react";

import { RewardsDestinationField } from "@/pages/Yaas/application/RewardsDestinationField";
import type { DistributionMode } from "@/pages/Yaas/application/types";

/** Connected wallet placeholder for the Rewards Destination field. */
const MOCK_CONNECTED_WALLET = "E9Ytfvreri1KbHhE84J7UQoPjphL4KLC1ZwfE8PXWi7x";

/** Read `?state=` from the URL on first render so design preview
 *  links can pin the Earn tab to a specific scenario without going
 *  through a real wallet flow. Currently the only recognized value
 *  is `no-yaas`, which flips the Enable / YaaS-Enabled button to
 *  its pre-opt-in state. Returns `null` for unknown values. */
function readEarnState(): "no-yaas" | null {
    if (typeof window === "undefined") return null;
    const value = new URLSearchParams(window.location.search).get("state");
    return value === "no-yaas" ? "no-yaas" : null;
}

/**
 * Rewards-destination settings surface extracted from the retired
 * positions experience.
 *
 * A form for distribution mode + rewards destination, with a "Verify
 * Ownership to Change" CTA. Reuses the YaaS application form's field
 * components so the field visuals stay in lockstep with the
 * apply/manage flow.
 *
 * It used to be two views — a flat list of position-type rows, and
 * these settings — toggled by a Back / Settings header above the
 * card. The list half and the header went when the last caller that
 * wanted them was archived: `ConfigureRoutingPanel` is the only
 * consumer left and it renders the settings body directly, so `view`
 * could never leave `"settings"`. Per-position routing-edit lives
 * upstream in `RetailYaasLandingPage`.
 */
/** Shape of the destinations/address-book entries. Matches the
 *  internal `Account` type used inside the component so external
 *  callers can lift the state up without coercing. */
export interface EarnContentDestination {
    id: string;
    uuid?: string;
    label: string;
    address: string;
    percentage: number;
    type: AccountType;
}

interface EarnContentProps {
    analytics?: ProductJourney;
    /** When true, swaps the settings surface from the
     *  raised-Card Enable YaaS flow to the bare-bg Destinations
     *  panel (H1 heading + divider + Rewards Routing table). */
    showPanelTabs?: boolean;
    /** Optional shared destinations state — when provided, EarnContent
     *  reads / writes this list instead of its internal `accounts`
     *  state, so callers (RetailYaasLandingPage) can render the same
     *  list in adjacent surfaces (per-position routing-edit view). */
    destinations?: EarnContentDestination[];
    onDestinationsChange?: (
        next:
            | EarnContentDestination[]
            | ((prev: EarnContentDestination[]) => EarnContentDestination[])
    ) => void;
    connectedWallet?: string;
    onCreateDestination?: (input: {
        name: string;
        address: string;
        analytics?: ProductJourney;
    }) => Promise<
        | { success: true; destination: EarnContentDestination }
        | { success: false; error: string }
    >;
    isCreatingDestination?: boolean;
}

export function EarnContent({
    analytics: entryAnalytics,
    showPanelTabs = false,
    destinations: externalDestinations,
    onDestinationsChange: externalSetDestinations,
    connectedWallet = MOCK_CONNECTED_WALLET,
    onCreateDestination,
    isCreatingDestination = false
}: EarnContentProps = {}): React.ReactElement {
    // Pin-state read once at mount. Drives the settings card between
    // its "already opted in" and pre-opt-in copy and CTAs, and gates
    // the unsaved-changes guard (see `effectiveDirty`). Real flow
    // eventually replaces this with the wallet's actual YaaS account
    // status from the session store.
    const earnState = useState(() => readEarnState())[0];
    const hasYaasPosition = earnState !== "no-yaas";
    const localAnalytics = useProductJourney(
        "saved_destination",
        "rewards_footer",
        connectedWallet
    );
    const analytics = entryAnalytics ?? localAnalytics;
    useProductObservation(analytics, "form_viewed");
    const [distributionMode, setDistributionMode] =
        useState<DistributionMode>("stream");
    const [rewardsDestination, setRewardsDestination] = useState("");
    const [accountLabel, setAccountLabel] = useState("");
    // Tracks focus on the freeform-text label input so the
    // placeholder clears the moment the user clicks in — same
    // pattern the RewardsDestinationField uses for the address
    // input. Empty when blurred so the placeholder reappears.
    const [isLabelFocused, setIsLabelFocused] = useState(false);
    // List of saved destination accounts. Pre-populated with the
    // connected wallet so the surface always shows at least one
    // account (the implicit default). "+Add Destination" appends the
    // current label + address to this list and clears both inputs.
    // Label is optional — empty string = no label.
    interface Account {
        /** Stable row identity. Keyed on this rather than `address`
         *  so duplicate-address rows (same wallet labelled twice)
         *  don't collide in the React key OR in the per-row routing
         *  handlers — clicking "All" on row N must only zero row N's
         *  neighbors, not every row that happens to share its
         *  address. */
        id: string;
        uuid?: string;
        label: string;
        address: string;
        /** Share of rewards routed to this address, 0–100. Source
         *  account starts at 100% so the default config is "everything
         *  back to me"; newly-added destinations come in at 0 so the
         *  user has to deliberately reallocate. */
        percentage: number;
        /** Account-type taxonomy for the leading badge. In design
         *  exploration this is hardcoded per-account; once verification
         *  is wired through the add flow it'll come from the
         *  `useSolanaAddressVerification` hook's owner classification. */
        type: AccountType;
    }
    const INITIAL_ACCOUNTS: Account[] = [
        {
            id: "source",
            uuid: undefined,
            label: "Source Account",
            address: connectedWallet,
            percentage: 100,
            type: "wallet"
        }
    ];
    const [internalAccounts, setInternalAccounts] =
        useState<Account[]>(INITIAL_ACCOUNTS);
    // If a parent passes shared destinations state, use that;
    // otherwise fall back to the internal state for standalone
    // consumers. Both branches operate on the same `Account[]` shape.
    const accounts = externalDestinations ?? internalAccounts;
    const setAccounts = useCallback(
        (next: Account[] | ((prev: Account[]) => Account[])): void => {
            if (externalSetDestinations) {
                externalSetDestinations(next);
            } else {
                setInternalAccounts(next);
            }
        },
        [externalSetDestinations]
    );
    const handleAddDestination = async (): Promise<void> => {
        analytics.send("submit_requested");
        const trimmedAddr = rewardsDestination.trim();
        if (!trimmedAddr) return;
        if (onCreateDestination) {
            const result = await onCreateDestination({
                analytics,
                name: accountLabel.trim() || trimmedAddr,
                address: trimmedAddr
            });
            if (!result.success) {
                toastManager.add({
                    title: "Destination was not added",
                    description: result.error,
                    data: { variant: "error" }
                });
                return;
            }
            setAccounts((prev) => [
                ...prev,
                {
                    ...result.destination,
                    percentage: 0
                }
            ]);
            setRewardsDestination("");
            setAccountLabel("");
            return;
        }
        setAccounts((prev) => [
            ...prev,
            {
                id: crypto.randomUUID(),
                uuid: undefined,
                label: accountLabel.trim(),
                address: trimmedAddr,
                percentage: 0,
                // Default newly-added rows to "new" — the address is
                // a structurally valid wallet shape (we won't have
                // gotten here otherwise). Future enhancement: read
                // the type off the verification hook's current
                // classification at add-time.
                type: "new"
            }
        ]);
        setRewardsDestination("");
        setAccountLabel("");
    };
    // Tier-3 verification for the in-progress account address.
    // Surfaced on the same row as the +Add Account button so the
    // user sees the on-chain hint without an extra vertical step
    // before deciding to add.
    const addressVerification =
        useSolanaAddressVerification(rewardsDestination);
    // +Add Account gate — disabled by default, only enables once the
    // address has cleared the on-chain check AND landed in a state
    // we'd accept as a destination. Empty input, in-flight checking,
    // RPC failure, and any hard-invalid classification (mint, frozen,
    // wrong-mint ATA) all stay disabled. "found" wallet/program/
    // USDv-ATA and "new" (first transfer creates the account) both
    // pass — the soft warning categories are still addable since the
    // user is the one acknowledging the address.
    const trimmedDestination = rewardsDestination.trim();
    const destinationShapeValid =
        trimmedDestination.length > 0 &&
        SolanaAddress.isShape(trimmedDestination);
    // Block duplicates — adding the same destination twice would
    // produce two rows the user has to keep in sync (and the
    // duplicate row would never resolve back to a single allocation
    // line in the underlying transfer). Compared on the raw address
    // string since base58 is case-sensitive on Solana.
    const destinationAlreadyAdded =
        destinationShapeValid &&
        accounts.some((a) => a.address === trimmedDestination);
    const canAddDestination =
        destinationShapeValid &&
        !destinationAlreadyAdded &&
        (addressVerification.status === "found" ||
            addressVerification.status === "new") &&
        !addressVerification.invalid;
    const verificationToneClass =
        addressVerification.tone === "error"
            ? "text-error-base"
            : addressVerification.tone === "success"
              ? "text-success-base"
              : addressVerification.tone === "warning"
                ? "text-warning-base"
                : addressVerification.tone === "muted"
                  ? "text-foreground-imprint"
                  : "text-foreground-neutral";
    // Routing must sum to exactly 100. When it doesn't, every
    // percentage input flips to the error border so the user sees
    // the constraint applies to the table as a whole, not to one
    // row. No row-level validation surface — the rows themselves
    // accept any 0–100 value individually.
    const routingTotal = accounts.reduce((sum, a) => sum + a.percentage, 0);
    const routingInvalid = routingTotal !== 100;

    // Committed snapshot — represents the last state the user actually
    // applied via "Verify Ownership to Change". Changes are batched
    // across both sub-tabs (Distribution + Rewards Destination) so the
    // user only signs a single ownership-proof transaction at the end.
    // Dirty = current form state diverges from this snapshot.
    const [committedDistributionMode, setCommittedDistributionMode] =
        useState<DistributionMode>("stream");
    const [committedAccounts, setCommittedAccounts] =
        useState<Account[]>(INITIAL_ACCOUNTS);
    const [showAppliedFlash, setShowAppliedFlash] = useState(false);
    const isDirty =
        distributionMode !== committedDistributionMode ||
        JSON.stringify(accounts) !== JSON.stringify(committedAccounts);
    // In the no-yaas / first-time-setup flow there's nothing for the
    // user to lose — the committed snapshot equals the defaults and
    // any "changes" are just the initial config. Suppress the
    // unsaved-changes warning + discard guard entirely in that mode.
    const effectiveDirty = isDirty && hasYaasPosition;
    // Reset every form input back to its committed snapshot. Used
    // by the unsaved-changes guard when the user confirms "Discard
    // Changes".
    const handleDiscardChanges = useCallback((): void => {
        setDistributionMode(committedDistributionMode);
        setAccounts(committedAccounts);
        setRewardsDestination("");
        setAccountLabel("");
    }, [committedDistributionMode, committedAccounts, setAccounts]);
    // Register dirty state with the app-level guard so any cross-page
    // navigation (NavigationBar, page-level tabs, browser unload)
    // triggers a confirm-discard modal before the user loses changes.
    useDirtyState(effectiveDirty, handleDiscardChanges);
    // Success flash auto-clears after 3s so the success indicator
    // doesn't linger past the moment of feedback.
    useEffect(() => {
        if (!showAppliedFlash) return;
        const timeout = setTimeout(() => setShowAppliedFlash(false), 3000);
        return (): void => clearTimeout(timeout);
    }, [showAppliedFlash]);
    const handleVerifyOwnership = (): void => {
        setCommittedDistributionMode(distributionMode);
        setCommittedAccounts(accounts);
        setShowAppliedFlash(true);
    };

    // Per-row copy feedback. Stores the most-recently-copied address;
    // the matching row shows "Copied" + the selected button state for
    // 2s, then reverts. Clicking a different row swaps the highlight
    // to that row (only the latest copy shows feedback, mirroring the
    // user's likely intent).
    const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
    useEffect(() => {
        if (!copiedAddress) return;
        const timeout = setTimeout(() => setCopiedAddress(null), 2000);
        return (): void => clearTimeout(timeout);
    }, [copiedAddress]);
    const handleCopyAddress = (address: string): void => {
        if (typeof navigator !== "undefined" && navigator.clipboard) {
            analytics.send("copy_requested");
            void navigator.clipboard
                .writeText(address)
                .then(() => analytics.send("copied"))
                .catch(() =>
                    analytics.send("copy_failed", {
                        error_code: "clipboard_unavailable"
                    })
                );
        }
        setCopiedAddress(address);
    };

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col">
                {showPanelTabs ? (
                    // Destinations panel — single view, no card
                    // chrome, no tab menu. H1 heading + divider
                    // sits flush with the surrounding bg. Form
                    // body (label input + address input + +Add
                    // Account + Rewards Routing table) renders
                    // directly on the page surface.
                    <>
                        {/* Set Destinations surface — flat on the
                            ground bg, no tabs and no raised-card
                            chrome. Per-position routing-edit lives
                            upstream (Mode 2's positions list →
                            Update sets `routingPositionSource` in
                            RetailYaasLandingPage), so this view is
                            now address-book-only: the Add Account
                            form + saved-destinations list. */}
                        <SectionHeading variant="h1">
                            Rewards Destinations
                        </SectionHeading>
                        <Text
                            variant="small"
                            render={<span />}
                            className="text-foreground-neutral block mt-6"
                        >
                            Add an account to route rewards to:
                        </Text>
                        <div className="mt-3 flex flex-col">
                            <input
                                type="text"
                                value={accountLabel}
                                onChange={(e) =>
                                    setAccountLabel(e.target.value)
                                }
                                onFocus={() => setIsLabelFocused(true)}
                                onBlur={() => setIsLabelFocused(false)}
                                placeholder={
                                    isLabelFocused
                                        ? ""
                                        : "Account Label (Optional)"
                                }
                                className={`${textVariants({ variant: "small" })} border border-line-base bg-transparent px-3 py-2 text-foreground-base placeholder:text-foreground-imprint focus:border-foreground-base focus:outline-none mb-2`}
                            />
                            <RewardsDestinationField
                                value={rewardsDestination}
                                onChange={setRewardsDestination}
                                state="default"
                                connectedWallet={connectedWallet}
                                showLabel={false}
                                placeholder="Account Address"
                                hideOnChainStatus
                            />
                            {/* Action row — verification status on
                                the left (top-aligned so a short or
                                wrapping message stays anchored to the
                                button's top edge), +Add Account on
                                the right. mt-2 matches the original
                                standalone button's offset from the
                                input above. */}
                            <div className="mt-2 flex items-start justify-between gap-3">
                                {destinationAlreadyAdded ? (
                                    // Duplicate-address branch wins over the
                                    // on-chain status text — the user has
                                    // already added this address, so the
                                    // verification result (which would
                                    // otherwise read "Valid account" etc.)
                                    // is less useful than the dedup signal.
                                    <Text
                                        variant="fine-print"
                                        render={<span />}
                                        className="text-error-base"
                                    >
                                        Address already added
                                    </Text>
                                ) : addressVerification.text ? (
                                    <Text
                                        variant="fine-print"
                                        render={<span />}
                                        className={verificationToneClass}
                                    >
                                        {addressVerification.text}
                                    </Text>
                                ) : (
                                    <span />
                                )}
                                <Button
                                    variant="sunken"
                                    interaction="lifted"
                                    size="sm"
                                    outline
                                    disabled={
                                        !canAddDestination ||
                                        isCreatingDestination
                                    }
                                    onClick={() => {
                                        void handleAddDestination();
                                    }}
                                >
                                    {isCreatingDestination
                                        ? "Adding..."
                                        : "+Add Account"}
                                </Button>
                            </div>
                            {/* Rewards Routing — single source of
                                truth for managing destination
                                accounts. Each row carries its
                                type badge, label/truncated-address,
                                copy affordance (with the "Copied"
                                asymmetric fade we used to put on
                                the now-removed Destination
                                Accounts list), a per-account
                                percentage input, and an "All"
                                shortcut. Split Evenly / Clear
                                footer wraps both rows of the
                                table. */}
                            <div className="mt-3">
                                <SectionHeading variant="body">
                                    Rewards Routing
                                </SectionHeading>
                                <TooltipProvider delay={500}>
                                    <div className="mt-2 flex flex-col gap-2">
                                        {accounts.map((account) => (
                                            <div
                                                key={account.id}
                                                className="flex items-center justify-between gap-3"
                                            >
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <AccountTypeBadge
                                                        type={account.type}
                                                        className="shrink-0"
                                                    />
                                                    <Tooltip>
                                                        <TooltipTrigger
                                                            render={
                                                                <span className="min-w-0 truncate" />
                                                            }
                                                        >
                                                            <Text
                                                                variant="number"
                                                                render={
                                                                    <span />
                                                                }
                                                                className="text-foreground-base"
                                                            >
                                                                {account.label ||
                                                                    `${account.address.slice(0, 6)}...${account.address.slice(-6)}`}
                                                            </Text>
                                                        </TooltipTrigger>
                                                        <TooltipContent
                                                            side="top"
                                                            maxWidth="lg"
                                                            className="whitespace-nowrap"
                                                        >
                                                            {account.address}
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </div>
                                                {/* Right cluster — "Copied"
                                                    asymmetric-fade tag fades
                                                    in to the LEFT of the
                                                    copy button. Destinations
                                                    tab is the address-book
                                                    layer (saved addresses
                                                    only); per-position %
                                                    controls live in the
                                                    Routing tab's edit
                                                    view. */}
                                                <div className="flex items-center gap-1 shrink-0">
                                                    <AnimatePresence
                                                        initial={false}
                                                    >
                                                        {copiedAddress ===
                                                            account.address && (
                                                            <motion.span
                                                                key="copied"
                                                                className={`${textVariants({ variant: "fine-print" })} text-accent-base shrink-0`}
                                                                initial={{
                                                                    opacity: 0
                                                                }}
                                                                animate={{
                                                                    opacity: 1,
                                                                    transition:
                                                                        {
                                                                            duration: 0.15,
                                                                            ease: "easeOut"
                                                                        }
                                                                }}
                                                                exit={{
                                                                    opacity: 0,
                                                                    transition:
                                                                        {
                                                                            duration: 0.4,
                                                                            ease: "easeIn"
                                                                        }
                                                                }}
                                                            >
                                                                Copied
                                                            </motion.span>
                                                        )}
                                                    </AnimatePresence>
                                                    <ButtonIcon
                                                        variant="transparent"
                                                        size="md"
                                                        className="shrink-0 duration-[400ms] ease-in aria-selected:duration-150 aria-selected:ease-out"
                                                        icon={<CopyIcon />}
                                                        onClick={() =>
                                                            handleCopyAddress(
                                                                account.address
                                                            )
                                                        }
                                                        aria-selected={
                                                            copiedAddress ===
                                                            account.address
                                                                ? "true"
                                                                : undefined
                                                        }
                                                        aria-label={`Copy ${account.address}`}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </TooltipProvider>
                            </div>
                        </div>
                        <div className="mt-15">
                            <Button
                                variant="raised"
                                interaction="light-bar"
                                size="lg"
                                fullWidth
                                disabled={routingInvalid}
                                onClick={handleVerifyOwnership}
                            >
                                {hasYaasPosition
                                    ? "Verify Ownership to Update"
                                    : "Verify Ownership to Start Earning"}
                            </Button>
                        </div>
                    </>
                ) : (
                    <Card
                        surface="raised"
                        border={false}
                        className="p-6 flex flex-col"
                    >
                        {!hasYaasPosition && (
                            <Text
                                variant="h1"
                                render={<span />}
                                className="text-foreground-base mb-4"
                            >
                                Enable Rewards
                            </Text>
                        )}
                        <Card surface="ground" className="p-4 flex flex-col">
                            {/* Distribution Method section — heading row
                        + toggle, grouped as a single section so
                        it sits parallel to the Rewards
                        Destination wrapper below. */}
                            <div>
                                {/* Heading row: title on the left, with the
                            right slot doubling as either a dirty /
                            applied status pill (when there's edit
                            state to surface) or the "Select one"
                            hint (default). Baseline-aligned so the
                            smaller hint text sits flush with the
                            bottom of the heading. */}
                                <div className="flex items-baseline justify-between gap-3">
                                    <Text
                                        variant="body"
                                        render={<span />}
                                        className="text-foreground-base"
                                    >
                                        Distribution Method
                                    </Text>
                                    {showAppliedFlash ? (
                                        <Text
                                            variant="small"
                                            render={<span />}
                                            className="text-success-base"
                                        >
                                            Changes applied
                                        </Text>
                                    ) : effectiveDirty ? (
                                        <Text
                                            variant="small"
                                            render={<span />}
                                            className="text-warning-base"
                                        >
                                            Unsaved changes
                                        </Text>
                                    ) : (
                                        <Text
                                            variant="small"
                                            render={<span />}
                                            className="text-foreground-neutral"
                                        >
                                            Select one
                                        </Text>
                                    )}
                                </div>
                                <div className="mt-2 flex flex-col gap-1">
                                    <List
                                        selectable="single"
                                        selected={distributionMode}
                                        onSelect={(key) =>
                                            setDistributionMode(
                                                key as DistributionMode
                                            )
                                        }
                                        className="flex flex-col"
                                    >
                                        <ListItem
                                            itemKey="stream"
                                            padding="py-3 px-2"
                                            align="start"
                                        >
                                            <div className="flex flex-col gap-0.5">
                                                <Text
                                                    variant="body"
                                                    render={<span />}
                                                    className="text-foreground-base"
                                                >
                                                    Stream
                                                </Text>
                                                <Text
                                                    variant="small"
                                                    render={<span />}
                                                    className="text-foreground-neutral"
                                                >
                                                    Rewards will stream directly
                                                    to the user
                                                </Text>
                                            </div>
                                        </ListItem>
                                        <ListItem
                                            itemKey="claim"
                                            padding="py-3 px-2"
                                            align="start"
                                        >
                                            <div className="flex flex-col gap-0.5">
                                                <Text
                                                    variant="body"
                                                    render={<span />}
                                                    className="text-foreground-base"
                                                >
                                                    Claim
                                                </Text>
                                                <Text
                                                    variant="small"
                                                    render={<span />}
                                                    className="text-foreground-neutral"
                                                >
                                                    User will claim the rewards
                                                    when they want
                                                </Text>
                                            </div>
                                        </ListItem>
                                    </List>
                                </div>
                            </div>

                            {/* Enable YaaS / first-time flow — simpler
                            form: optional label input + address
                            input pre-filled with the connected
                            wallet. The "Add more after enabling"
                            helper signals that the routing-table
                            experience (multiple destinations,
                            percentage allocations) unlocks once
                            the user opts in. */}
                            <div className="mt-10">
                                <Text
                                    variant="body"
                                    render={<span />}
                                    className="text-foreground-base"
                                >
                                    Rewards Destination (Optional)
                                </Text>
                                <div className="mt-2 flex flex-col">
                                    <input
                                        type="text"
                                        value={accountLabel}
                                        onChange={(e) =>
                                            setAccountLabel(e.target.value)
                                        }
                                        onFocus={() => setIsLabelFocused(true)}
                                        onBlur={() => setIsLabelFocused(false)}
                                        placeholder={
                                            isLabelFocused
                                                ? ""
                                                : "Source Account"
                                        }
                                        className={`${textVariants({ variant: "small" })} border border-line-base bg-transparent px-3 py-2 text-foreground-base placeholder:text-foreground-imprint focus:border-foreground-base focus:outline-none mb-2`}
                                    />
                                    <RewardsDestinationField
                                        value={rewardsDestination}
                                        onChange={setRewardsDestination}
                                        state="default"
                                        connectedWallet={connectedWallet}
                                        showLabel={false}
                                    />
                                    <Text
                                        variant="fine-print"
                                        render={<span />}
                                        className="text-foreground-neutral mt-4"
                                    >
                                        Add more Rewards Destinations after
                                        enabling.
                                    </Text>
                                </div>
                            </div>
                        </Card>
                        <div className="mt-6">
                            <Button
                                variant="raised"
                                interaction="light-bar"
                                size="lg"
                                fullWidth
                                onClick={handleVerifyOwnership}
                            >
                                {hasYaasPosition
                                    ? "Verify Ownership to Update"
                                    : "Verify Ownership to Start Earning"}
                            </Button>
                        </div>
                    </Card>
                )}
            </div>
        </div>
    );
}
